import React, { useState, useEffect, useCallback } from 'react';
import './RegData.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDatabase,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faSpinner,
  faRefresh,
  faQrcode,
  faCheck,
  faXmark,
  faDownload,
  faFilter,
  faSort,
  faSortUp,
  faSortDown,
  faLock,
  faKey,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';

const API_BASE = process.env.REACT_APP_API_URL || 'https://khamma-ghani-server.vercel.app';
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function RegData() {

  /* ── Auth State ── */
  const [apiKey, setApiKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  /* ── Data State ── */
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* ── Pagination ── */
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* ── Search & Sort ── */
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  /* ── Check stored API key on mount ── */
  useEffect(() => {
    const storedKey = sessionStorage.getItem('rca_admin_key');
    if (storedKey) {
      verifyApiKey(storedKey, true);
    }
  }, []);

  /* ── Verify API Key ── */
  const verifyApiKey = async (key, silent = false) => {
    if (!silent) setAuthLoading(true);
    setAuthError('');
    try {
      const res = await fetch(`${API_BASE}/api/verify-api-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: key }),
      });
      const data = await res.json();
      if (data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('rca_admin_key', key);
      } else {
        setAuthError(data.message || 'Invalid API key');
        sessionStorage.removeItem('rca_admin_key');
      }
    } catch (err) {
      setAuthError('Network error. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAuthSubmit = (e) => {
    e.preventDefault();
    if (!apiKey.trim()) {
      setAuthError('Please enter an API key');
      return;
    }
    verifyApiKey(apiKey.trim());
  };

  /* ── Fetch Data ── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/registration/all`);
      const json = await res.json();
      if (json.success) {
        setData(json.registrations || []);
      } else {
        throw new Error(json.message || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── Fetch data when authenticated ── */
  useEffect(() => { 
    if (isAuthenticated) {
      fetchData(); 
    }
  }, [fetchData, isAuthenticated]);

  /* ── Filter & Sort ── */
  useEffect(() => {
    let result = [...data];

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(r =>
        r.name?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.phone?.includes(q) ||
        r.roll?.toLowerCase().includes(q) ||
        r.hall?.toLowerCase().includes(q) ||
        r.qr_code?.toLowerCase().includes(q)
      );
    }

    // Sort
    result.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      // Handle dates
      if (sortField === 'created_at') {
        valA = new Date(valA || 0).getTime();
        valB = new Date(valB || 0).getTime();
      }
      // Handle numbers
      else if (typeof valA === 'number') {
        // Already numbers
      }
      // Handle strings
      else {
        valA = (valA || '').toString().toLowerCase();
        valB = (valB || '').toString().toLowerCase();
      }

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredData(result);
    setCurrentPage(1);
  }, [data, search, sortField, sortDir]);

  /* ── Pagination Calculations ── */
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIdx = (currentPage - 1) * rowsPerPage;
  const endIdx = startIdx + rowsPerPage;
  const pageData = filteredData.slice(startIdx, endIdx);

  /* ── Sort Handler ── */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  /* ── Export CSV ── */
  const exportCSV = () => {
    const headers = ['ID', 'Name', 'Email', 'Phone', 'Roll', 'Hall', 'Passes', 'Amount', 'Scanned', 'QR Code', 'Payment ID', 'Created'];
    const rows = filteredData.map(r => [
      r.id,
      r.name,
      r.email,
      r.phone,
      r.roll,
      r.hall,
      r.passes,
      r.amount,
      r.scanned_count,
      r.qr_code,
      r.payment_id,
      r.created_at ? new Date(r.created_at).toLocaleString() : ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${(cell ?? '').toString().replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `registrations_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  /* ── Render Sort Icon ── */
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <FontAwesomeIcon icon={faSort} className="sort-icon neutral" />;
    return <FontAwesomeIcon icon={sortDir === 'asc' ? faSortUp : faSortDown} className="sort-icon active" />;
  };

  /* ── Format Date ── */
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  /* ── Logout ── */
  const handleLogout = () => {
    setIsAuthenticated(false);
    setApiKey('');
    setData([]);
    sessionStorage.removeItem('rca_admin_key');
  };

  /* ══════════════════════════════════════
     RENDER: Auth Screen
     ══════════════════════════════════════ */
  if (!isAuthenticated) {
    return (
      <div className="rd-wrapper rd-auth-wrapper">
        <div className="rd-auth-card">
          <div className="rd-auth-icon">
            <FontAwesomeIcon icon={faLock} />
          </div>
          <h1>Registration Data Access</h1>
          <p>Enter the admin API key to view registration data</p>

          <form onSubmit={handleAuthSubmit} className="rd-auth-form">
            <div className="rd-auth-input-wrap">
              <FontAwesomeIcon icon={faKey} className="input-icon" />
              <input
                type="password"
                placeholder="Enter API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoFocus
              />
            </div>
            {authError && (
              <div className="rd-auth-error">
                <FontAwesomeIcon icon={faXmark} /> {authError}
              </div>
            )}
            <button type="submit" disabled={authLoading} className="rd-auth-btn">
              {authLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <>
                  Access Data
                  <FontAwesomeIcon icon={faArrowRight} />
                </>
              )}
            </button>
          </form>

          <div className="rd-auth-footer">
            <p>Contact admin for access credentials</p>
          </div>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════
     RENDER: Main Data View
     ══════════════════════════════════════ */
  return (
    <div className="rd-wrapper">
      <div className="rd-container">
        {/* ── Header ── */}
        <div className="rd-header">
          <div className="rd-title">
            <FontAwesomeIcon icon={faDatabase} className="rd-icon" />
            <div>
              <h1>Registration Data</h1>
              <p>Jaisalmer — The Aurial Mirage</p>
            </div>
          </div>
          <div className="rd-header-right">
            <div className="rd-stats">
              <div className="rd-stat">
                <span className="rd-stat-num">{data.length}</span>
                <span className="rd-stat-label">Total Registrations</span>
              </div>
              <div className="rd-stat">
                <span className="rd-stat-num">{data.reduce((sum, r) => sum + (r.passes || 0), 0)}</span>
                <span className="rd-stat-label">Total Passes</span>
              </div>
              <div className="rd-stat">
                <span className="rd-stat-num">₹{data.reduce((sum, r) => sum + (r.amount || 0), 0).toLocaleString()}</span>
                <span className="rd-stat-label">Total Revenue</span>
              </div>
            </div>
            <button className="rd-logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="rd-toolbar">
          <div className="rd-search">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, phone, roll, hall..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="rd-actions">
            <button className="rd-btn outline" onClick={fetchData} disabled={loading}>
              <FontAwesomeIcon icon={faRefresh} spin={loading} />
              Refresh
            </button>
            <button className="rd-btn primary" onClick={exportCSV} disabled={filteredData.length === 0}>
              <FontAwesomeIcon icon={faDownload} />
              Export CSV
            </button>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="rd-table-wrap">
          {loading ? (
            <div className="rd-loading">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Loading registrations...</span>
            </div>
          ) : error ? (
            <div className="rd-error">
              <FontAwesomeIcon icon={faXmark} />
              <span>{error}</span>
              <button onClick={fetchData}>Retry</button>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="rd-empty">
              <FontAwesomeIcon icon={faFilter} />
              <span>{search ? 'No matching registrations found' : 'No registrations yet'}</span>
            </div>
          ) : (
            <table className="rd-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('id')}># <SortIcon field="id" /></th>
                  <th onClick={() => handleSort('name')}>Name <SortIcon field="name" /></th>
                  <th onClick={() => handleSort('email')}>Email <SortIcon field="email" /></th>
                  <th onClick={() => handleSort('phone')}>Phone <SortIcon field="phone" /></th>
                  <th onClick={() => handleSort('roll')}>Roll <SortIcon field="roll" /></th>
                  <th onClick={() => handleSort('hall')}>Hall <SortIcon field="hall" /></th>
                  <th onClick={() => handleSort('passes')}>Passes <SortIcon field="passes" /></th>
                  <th onClick={() => handleSort('amount')}>Amount <SortIcon field="amount" /></th>
                  <th onClick={() => handleSort('scanned_count')}>Scanned <SortIcon field="scanned_count" /></th>
                  <th>QR Code</th>
                  <th onClick={() => handleSort('created_at')}>Date <SortIcon field="created_at" /></th>
                </tr>
              </thead>
              <tbody>
                {pageData.map((reg, idx) => (
                  <tr key={reg.id || idx}>
                    <td className="td-id">{reg.id}</td>
                    <td className="td-name">{reg.name}</td>
                    <td className="td-email">{reg.email}</td>
                    <td className="td-phone">{reg.phone}</td>
                    <td className="td-roll">{reg.roll}</td>
                    <td className="td-hall">{reg.hall}</td>
                    <td className="td-passes">{reg.passes}</td>
                    <td className="td-amount">₹{reg.amount}</td>
                    <td className="td-scanned">
                      <span className={`scan-badge ${reg.scanned_count >= reg.passes ? 'full' : reg.scanned_count > 0 ? 'partial' : ''}`}>
                        {reg.scanned_count}/{reg.passes}
                      </span>
                    </td>
                    <td className="td-qr">
                      <code className="qr-code">{reg.qr_code?.slice(0, 15)}...</code>
                    </td>
                    <td className="td-date">{formatDate(reg.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Pagination ── */}
        {!loading && !error && filteredData.length > 0 && (
          <div className="rd-pagination">
            <div className="rd-page-info">
              Showing {startIdx + 1}-{Math.min(endIdx, filteredData.length)} of {filteredData.length} registrations
            </div>
            <div className="rd-page-controls">
              <select
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="rd-rows-select"
              >
                {ROWS_PER_PAGE_OPTIONS.map(n => (
                  <option key={n} value={n}>{n} per page</option>
                ))}
              </select>

              <div className="rd-page-btns">
                <button
                  className="rd-page-btn"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button
                  className="rd-page-btn"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>

                <span className="rd-page-num">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  className="rd-page-btn"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
                <button
                  className="rd-page-btn"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
